import type { SendMessageParams } from '../../types';
import type {
  ApiAttachment,
  ApiChat,
  ApiContact,
  ApiDice,
  ApiInputReplyInfo,
  ApiInputSuggestedPostInfo,
  ApiMediaTodo,
  ApiMessage,
  ApiMessageEntity,
  ApiMessagePoll,
  ApiNewMediaTodo,
  ApiNewPoll,
  ApiPeer,
  ApiPollResult,
  ApiReplyInfo,
  ApiSticker,
  ApiStory,
  ApiStorySkipped,
  ApiVideo,
  MediaContent,
} from '../types';
import { ApiMessageEntityTypes, MAIN_THREAD_ID } from '../types';

import {
  SUPPORTED_AUDIO_CONTENT_TYPES,
  SUPPORTED_PHOTO_CONTENT_TYPES,
  SUPPORTED_VIDEO_CONTENT_TYPES,
} from '../../config';
import { getEmojiOnlyCountForMessage } from '../../global/helpers/getEmojiOnlyCountForMessage';
import { addTimestampEntities } from '../../util/dates/timestamp';
import { omitUndefined } from '../../util/iteratees';
import { getNextLocalMessageId } from '../../util/keys/messageKey';
import { getServerTime } from '../../util/serverTime';
import { interpolateArray } from '../../util/waveform';
import { sendApiUpdate } from '../updates/apiUpdateEmitter';

export const LOCAL_MEDIA_UPLOADING_TEMP_ID = 'temp';
const INPUT_WAVEFORM_LENGTH = 63;

let currentUserId!: string;

export function setMessageBuilderCurrentUserId(_currentUserId: string) {
  currentUserId = _currentUserId;
}

export function getMessageBuilderCurrentUserId() {
  return currentUserId;
}

export function sendMessageLocal(
  params: SendMessageParams,
) {
  const {
    chat, text, entities, replyInfo, suggestedPostInfo, attachment, sticker, story, gif, poll, todo,
    contact, scheduledAt, scheduleRepeatPeriod, groupedId, sendAs, wasDrafted, isInvertedMedia, effectId, isPending,
    messagePriceInStars, dice,
  } = params;

  if (!chat) return undefined;

  const {
    message: localMessage,
    poll: localPoll,
  } = buildLocalMessage({
    chat,
    text,
    entities,
    replyInfo,
    suggestedPostInfo,
    attachment,
    sticker,
    gif,
    poll,
    todo,
    contact,
    groupedId,
    scheduledAt,
    scheduleRepeatPeriod,
    sendAs,
    story,
    isInvertedMedia,
    effectId,
    isPending,
    messagePriceInStars,
    dice,
  });

  sendApiUpdate({
    '@type': localMessage.isScheduled ? 'newScheduledMessage' : 'newMessage',
    id: localMessage.id,
    chatId: chat.id,
    message: localMessage,
    poll: localPoll,
    wasDrafted,
  });

  return Promise.resolve(localMessage);
}

export function buildLocalMessage({
  chat,
  text,
  entities,
  replyInfo,
  suggestedPostInfo,
  attachment,
  sticker,
  gif,
  poll,
  todo,
  contact,
  groupedId,
  scheduledAt,
  scheduleRepeatPeriod,
  sendAs,
  story,
  isInvertedMedia,
  effectId,
  isPending,
  messagePriceInStars,
  dice,
}: {
  chat: ApiChat;
  text?: string;
  entities?: ApiMessageEntity[];
  replyInfo?: ApiInputReplyInfo;
  suggestedPostInfo?: ApiInputSuggestedPostInfo;
  attachment?: ApiAttachment;
  sticker?: ApiSticker;
  gif?: ApiVideo;
  poll?: ApiNewPoll;
  todo?: ApiNewMediaTodo;
  contact?: ApiContact;
  groupedId?: string;
  scheduledAt?: number;
  scheduleRepeatPeriod?: number;
  sendAs?: ApiPeer;
  story?: ApiStory | ApiStorySkipped;
  isInvertedMedia?: true;
  effectId?: string;
  isPending?: true;
  messagePriceInStars?: number;
  dice?: string;
}) {
  const localId = getNextLocalMessageId();
  const media = attachment && buildUploadingMedia(attachment);

  const resultReplyInfo = replyInfo && buildReplyInfo(replyInfo, chat.isForum);

  const localPoll = poll && buildNewLocalPoll(poll);
  const localTodo = todo && buildNewLocalTodo(todo);

  const localDice = dice ? {
    mediaType: 'dice',
    value: -1,
    emoticon: dice,
  } satisfies ApiDice : undefined;

  const formattedText = text && !dice ? addTimestampEntities(
    { text, entities, emojiOnlyCount: undefined },
  ) : undefined;

  const message = {
    id: localId,
    chatId: chat.id,
    content: omitUndefined({
      text: formattedText,
      ...media,
      sticker,
      video: gif || media?.video,
      contact,
      storyData: story && { mediaType: 'storyData', ...story },
      pollId: localPoll?.summary.id,
      todo: localTodo,
      dice: localDice,
    }),
    date: scheduledAt || getServerTime(),
    // The current user's own posts (including channel posts) always render as outgoing here, so the
    // pending message must match the server echo — otherwise a channel post briefly shows as incoming
    isOutgoing: true,
    senderId: chat.type !== 'chatTypePrivate' ? (sendAs?.id || currentUserId) : undefined,
    replyInfo: resultReplyInfo,
    suggestedPostInfo,
    ...(groupedId && {
      groupedId,
      ...(media && (media.photo || media.video) && { isInAlbum: true }),
    }),
    ...(scheduledAt && { isScheduled: true }),
    scheduleRepeatPeriod,
    isForwardingAllowed: true,
    isInvertedMedia,
    effectId,
    ...(isPending && { sendingState: 'messageSendingStatePending' }),
    ...(messagePriceInStars && { paidMessageStars: messagePriceInStars }),
  } satisfies ApiMessage;

  const emojiOnlyCount = getEmojiOnlyCountForMessage(message.content, message.groupedId);
  if (emojiOnlyCount && message.content.text) message.content.text.emojiOnlyCount = emojiOnlyCount;

  return {
    message,
    poll: localPoll,
  };
}

export function buildLocalForwardedMessage({
  toChat,
  toThreadId,
  message,
  scheduledAt,
  scheduleRepeatPeriod,
  noAuthors,
  noCaptions,
  isCurrentUserPremium,
  sendAs,
  effectId,
}: {
  toChat: ApiChat;
  toThreadId?: number;
  message: ApiMessage;
  scheduledAt?: number;
  scheduleRepeatPeriod?: number;
  noAuthors?: boolean;
  noCaptions?: boolean;
  isCurrentUserPremium?: boolean;
  sendAs?: ApiPeer;
  effectId?: string;
}): ApiMessage {
  const localId = getNextLocalMessageId();
  const {
    content,
    chatId: fromChatId,
    id: fromMessageId,
    senderId,
    groupedId,
    isInAlbum,
    isInvertedMedia,
  } = message;

  const isAudio = content.audio;
  const asIncomingInChatWithSelf = (
    toChat.id === currentUserId && (fromChatId !== toChat.id || message.forwardInfo) && !isAudio
  );
  const shouldHideText = Object.keys(content).length > 1 && content.text && noCaptions;
  const shouldDropCustomEmoji = !isCurrentUserPremium;
  const strippedText = content.text?.entities && shouldDropCustomEmoji ? {
    text: content.text.text,
    entities: content.text.entities.filter((entity) => entity.type !== ApiMessageEntityTypes.CustomEmoji),
  } : content.text;
  const textWithTimestamps = strippedText && addTimestampEntities(strippedText);
  const emojiOnlyCount = getEmojiOnlyCountForMessage(content, groupedId);
  if (emojiOnlyCount && textWithTimestamps) textWithTimestamps.emojiOnlyCount = emojiOnlyCount;

  const updatedContent = {
    ...content,
    text: !shouldHideText ? textWithTimestamps : undefined,
  };

  // TODO Prepare reply info between forwarded messages locally, to prevent height jumps
  const isToMainThread = toThreadId === MAIN_THREAD_ID;
  const replyInfo: ApiReplyInfo | undefined = toThreadId && !isToMainThread ? {
    type: 'message',
    replyToMsgId: toThreadId,
    replyToTopId: toThreadId,
    isForumTopic: toChat.isForum || undefined,
  } : undefined;

  return {
    id: localId,
    chatId: toChat.id,
    content: updatedContent,
    date: scheduledAt || getServerTime(),
    scheduleRepeatPeriod,
    isOutgoing: !asIncomingInChatWithSelf && toChat.type !== 'chatTypeChannel',
    senderId: toChat.type !== 'chatTypePrivate' ? (sendAs?.id || currentUserId) : undefined,
    sendingState: 'messageSendingStatePending',
    groupedId,
    isInAlbum,
    isForwardingAllowed: true,
    replyInfo,
    isInvertedMedia,
    effectId,
    ...(toThreadId && toChat?.isForum && { isTopicReply: true }),

    // Forward info doesn't get added when user forwards own messages and when forwarding audio
    ...(message.chatId !== currentUserId && !isAudio && !noAuthors && {
      forwardInfo: {
        date: message.forwardInfo?.date || message.date,
        savedDate: message.date,
        isChannelPost: false,
        fromChatId,
        fromMessageId,
        fromId: senderId,
        savedFromPeerId: message.chatId,
      },
    }),
    ...(message.chatId === currentUserId && !noAuthors && { forwardInfo: message.forwardInfo }),
    ...(scheduledAt && { isScheduled: true }),
  };
}

function buildReplyInfo(inputInfo: ApiInputReplyInfo, isForum?: boolean): ApiReplyInfo {
  if (inputInfo.type === 'story') {
    return {
      type: 'story',
      peerId: inputInfo.peerId,
      storyId: inputInfo.storyId,
    };
  }

  return {
    type: 'message',
    replyToMsgId: inputInfo.replyToMsgId,
    replyToTopId: inputInfo.replyToTopId,
    replyToPeerId: inputInfo.replyToPeerId,
    quoteText: inputInfo.quoteText,
    quoteOffset: inputInfo.quoteOffset,
    isForumTopic: isForum && inputInfo.replyToTopId ? true : undefined,
    ...(Boolean(inputInfo.quoteText) && { isQuote: true }),
  };
}

export function buildUploadingMedia(
  attachment: ApiAttachment,
): MediaContent {
  if (attachment.gif) {
    return { video: attachment.gif };
  }

  const {
    filename: fileName,
    blobUrl,
    previewBlobUrl,
    mimeType,
    size,
    audio,
    shouldSendAsFile,
    shouldSendAsSpoiler,
    ttlSeconds,
  } = attachment;

  if (!shouldSendAsFile) {
    if (attachment.quick) {
      // TODO Handle GIF as video, but support playback in <video>
      if (SUPPORTED_PHOTO_CONTENT_TYPES.has(mimeType)) {
        const { width, height } = attachment.quick;
        return {
          photo: {
            mediaType: 'photo',
            id: LOCAL_MEDIA_UPLOADING_TEMP_ID,
            sizes: [],
            thumbnail: { width, height, dataUri: previewBlobUrl || blobUrl },
            blobUrl,
            date: Math.round(Date.now() / 1000),
            isSpoiler: shouldSendAsSpoiler,
          },
        };
      }
      if (SUPPORTED_VIDEO_CONTENT_TYPES.has(mimeType)) {
        const { width, height, duration } = attachment.quick;
        return {
          video: {
            mediaType: 'video',
            id: LOCAL_MEDIA_UPLOADING_TEMP_ID,
            mimeType,
            duration: duration || 0,
            fileName,
            width,
            height,
            blobUrl,
            ...(previewBlobUrl && { thumbnail: { width, height, dataUri: previewBlobUrl } }),
            size,
            isSpoiler: shouldSendAsSpoiler,
          },
        };
      }
    }
    if (attachment.videoNote) {
      // Round video message — the optimistic bubble has to be a circle too, or the message jumps
      // from a rectangle to a circle when the server echo lands.
      const { duration, width, height } = attachment.videoNote;
      return {
        video: {
          mediaType: 'video',
          id: LOCAL_MEDIA_UPLOADING_TEMP_ID,
          mimeType,
          duration,
          fileName,
          width,
          height,
          blobUrl,
          isRound: true,
          ...(previewBlobUrl && { thumbnail: { width, height, dataUri: previewBlobUrl } }),
          size,
        },
        ttlSeconds,
      };
    }
    if (attachment.voice) {
      const { duration, waveform } = attachment.voice;
      const { data: inputWaveform } = interpolateArray(waveform, INPUT_WAVEFORM_LENGTH);
      return {
        voice: {
          mediaType: 'voice',
          id: LOCAL_MEDIA_UPLOADING_TEMP_ID,
          duration,
          waveform: inputWaveform,
          size,
          isTrainingRecording: attachment.isTrainingRecording,
        },
        ttlSeconds,
      };
    }
    if (SUPPORTED_AUDIO_CONTENT_TYPES.has(mimeType)) {
      const { duration, performer, title } = audio || {};
      return {
        audio: {
          mediaType: 'audio',
          id: LOCAL_MEDIA_UPLOADING_TEMP_ID,
          mimeType,
          fileName,
          size,
          duration: duration || 0,
          title,
          performer,
          isTrainingRecording: attachment.isTrainingRecording,
        },
      };
    }
  }
  return {
    document: {
      mediaType: 'document',
      mimeType,
      fileName,
      size,
      ...(previewBlobUrl && { previewBlobUrl }),
    },
  };
}

function buildNewLocalPoll(poll: ApiNewPoll): ApiMessagePoll {
  const resultByOption = poll.correctAnswers?.length
    ? poll.summary.answers.reduce((acc, answer, index) => {
      const isCorrect = poll.correctAnswers?.includes(index);

      acc[answer.option] = {
        option: answer.option,
        votersCount: 0,
        isCorrect: isCorrect ? true : undefined,
      };

      return acc;
    }, {} as Record<string, ApiPollResult>)
    : undefined;

  return {
    mediaType: 'poll',
    summary: poll.summary,
    results: {
      resultByOption,
      solution: poll.solution,
      solutionEntities: poll.solutionEntities,
      solutionMedia: poll.solutionMedia ? buildUploadingMedia(poll.solutionMedia) : undefined,
    },
    attachedMedia: poll.attachedMedia ? buildUploadingMedia(poll.attachedMedia) : undefined,
  };
}

function buildNewLocalTodo(todo: ApiNewMediaTodo): ApiMediaTodo {
  return {
    mediaType: 'todo',
    todo: todo.todo,
  };
}
